import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { fetchAnalytics } from '../lib/api';
import { DEPARTMENT_MAP, TYPE_MAP } from '../lib/constants';
import Spinner from '../components/Spinner';
import { cn } from '../lib/utils';

const fmtH = (h) => (h == null ? '—' : h >= 48 ? `${(h / 24).toFixed(1)}d` : `${h}h`);

function Stat({ label, value, sub, tone }) {
  return (
    <div className="rounded-xl border border-border bg-card p-4">
      <p className="text-xs text-muted-foreground">{label}</p>
      <p className={cn('mt-1 text-2xl font-semibold tabular-nums', tone)}>{value}</p>
      {sub ? <p className="text-xs text-muted-foreground">{sub}</p> : null}
    </div>
  );
}

function BarRow({ label, value, max, color }) {
  const pct = max ? Math.round((value / max) * 100) : 0;
  return (
    <div className="flex items-center gap-2 text-xs">
      <span className="w-24 shrink-0 truncate text-muted-foreground sm:w-32">{label}</span>
      <div className="h-3 flex-1 overflow-hidden rounded bg-muted">
        <div className={cn('h-full rounded', color || 'bg-primary')} style={{ width: `${pct}%` }} />
      </div>
      <span className="w-8 shrink-0 text-right tabular-nums">{value}</span>
    </div>
  );
}

export default function AnalyticsPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    fetchAnalytics().then(setData).catch(() => setData(null)).finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="p-6"><Spinner /></div>;
  if (!data) return <div className="p-6 text-sm text-muted-foreground">Couldn’t load analytics.</div>;

  const weekMax = Math.max(1, ...data.by_week.flatMap((w) => [w.opened, w.closed]));
  const deptEntries = Object.entries(data.by_department).sort((a, b) => b[1] - a[1]);
  const deptMax = Math.max(1, ...deptEntries.map(([, c]) => c));
  const typeEntries = Object.entries(data.by_type).sort((a, b) => b[1] - a[1]);
  const typeMax = Math.max(1, ...typeEntries.map(([, c]) => c));

  return (
    <div className="space-y-4 p-4 sm:p-6">
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
        <Stat label="open" value={data.totals.open} />
        <Stat label="done" value={data.totals.done} tone="text-emerald-500" />
        <Stat label="overdue" value={data.totals.overdue} tone={data.totals.overdue ? 'text-red-500' : ''} />
        <Stat label="opened · 30d" value={data.opened_30d} />
        <Stat label="closed · 30d" value={data.closed_30d} />
        <Stat label="avg to close" value={fmtH(data.avg_ttc_hours)} sub={`median ${fmtH(data.median_ttc_hours)}`} />
      </div>

      {/* Wide screens: chart across two thirds with the recently-closed list
          beside it spanning both rows; breakdowns under the chart. Narrow
          screens stack chart → breakdowns → recently closed. */}
      <div className="grid gap-4 xl:grid-cols-3">
        <div className="rounded-xl border border-border bg-card p-4 xl:col-span-2">
          <h2 className="mb-3 font-mono text-sm text-muted-foreground">{'// opened vs closed — last 8 weeks'}</h2>
          <div className="flex items-end gap-3" style={{ height: 150 }}>
            {data.by_week.map((w) => (
              <div key={w.label} className="flex flex-1 flex-col items-center gap-1">
                <div className="flex w-full items-end justify-center gap-1" style={{ height: 120 }}>
                  <div className="w-4 rounded-t bg-sky-500" style={{ height: `${(w.opened / weekMax) * 100}%` }} title={`${w.opened} opened`} />
                  <div className="w-4 rounded-t bg-emerald-500" style={{ height: `${(w.closed / weekMax) * 100}%` }} title={`${w.closed} closed`} />
                </div>
                <span className="text-[9px] text-muted-foreground">{w.label.slice(5)}</span>
              </div>
            ))}
          </div>
          <div className="mt-2 flex gap-4 text-xs text-muted-foreground">
            <span className="flex items-center gap-1"><span className="h-2 w-2 rounded bg-sky-500" /> opened</span>
            <span className="flex items-center gap-1"><span className="h-2 w-2 rounded bg-emerald-500" /> closed</span>
          </div>
        </div>

        <div className="order-last rounded-xl border border-border bg-card p-4 xl:order-none xl:row-span-2">
          <h2 className="mb-3 font-mono text-sm text-muted-foreground">{'// recently closed'}</h2>
          {data.recently_closed.length ? (
            <div className="divide-y divide-border">
              {data.recently_closed.map((t) => (
                <button
                  key={t.id}
                  onClick={() => navigate(`/tickets/${t.id}`)}
                  className="flex w-full items-center justify-between gap-3 rounded px-1 py-1.5 text-left text-sm transition hover:bg-accent/40"
                >
                  <span className="min-w-0 truncate"><span className="text-muted-foreground">#{t.id}</span> {t.title}</span>
                  <span className="shrink-0 text-xs text-muted-foreground">closed in {fmtH(t.ttc_hours)}</span>
                </button>
              ))}
            </div>
          ) : <p className="text-sm text-muted-foreground">Nothing closed yet.</p>}
        </div>

        <div className="rounded-xl border border-border bg-card p-4">
          <h2 className="mb-3 font-mono text-sm text-muted-foreground">{'// by department'}</h2>
          <div className="space-y-1.5">
            {deptEntries.map(([k, c]) => <BarRow key={k} label={DEPARTMENT_MAP[k]?.label || k} value={c} max={deptMax} />)}
          </div>
        </div>
        <div className="rounded-xl border border-border bg-card p-4">
          <h2 className="mb-3 font-mono text-sm text-muted-foreground">{'// by type'}</h2>
          <div className="space-y-1.5">
            {typeEntries.map(([k, c]) => <BarRow key={k} label={TYPE_MAP[k]?.label || k} value={c} max={typeMax} color="bg-violet-500" />)}
          </div>
        </div>
      </div>
    </div>
  );
}
